/**
 * Sewing Floor Board — the filter declaration, and the URL state that is not a filter.
 * See `design-notes.md` for the floor list's provenance and the cascade.
 *
 * Mirrors the `filters:` block of `winsight-server`'s `reports/sewing-floor-board/report.yaml`,
 * which is authoritative: `tran_date`, `factory`, `building`, `floor` and `metric` are all required
 * and all bind as equality. Nothing here is multi-valued.
 */

import { format, subDays } from 'date-fns';
import { z } from 'zod';

import { FACTORY_OPTIONS, type FactoryCode } from '@/lib/factories';
import type { FilterOption, FilterSchema } from '@/lib/filters';

const ISO = 'yyyy-MM-dd';

/**
 * Local hour when the wall boards roll from "previous working day" to "today".
 * Before this hour, morning reviews still look at yesterday's close; from 09:00 the day is live.
 */
export const BOARD_DAY_ROLLOVER_HOUR = 9;

/**
 * The day a sewing wall board shows by default.
 *
 * Before 09:00 local time → previous working day (Sunday is not a working day, so Monday morning
 * lands on Saturday). From 09:00 → calendar today. Resolved on every call, not at module load, so a
 * kiosk left open across the rollover picks up the new day on the next wall-poll tick (see
 * `useBoardDay`).
 */
export function defaultDate(now: Date = new Date()): string {
  const day = now.getHours() < BOARD_DAY_ROLLOVER_HOUR ? previousWorkingDay(now) : now;
  return format(day, ISO);
}

/** Calendar day before `from`, skipping Sunday. Saturday is a working day on these boards. */
function previousWorkingDay(from: Date): Date {
  let day = subDays(from, 1);
  if (day.getDay() === 0) {
    day = subDays(day, 1);
  }
  return day;
}

export interface FloorEntry {
  building: string;
  floor: string;
  /** Legacy's own dropdown label. */
  label: string;
}

/**
 * Sewing floors per factory — see the header note for provenance.
 * Shared with sewing-factory-board multi-selects so both boards name the same rooms.
 */
export const SEWING_FLOORS: Record<FactoryCode, readonly FloorEntry[]> = {
  BCA: [
    { building: 'A', floor: '1', label: 'A-1 Sewing' },
    { building: 'A', floor: '2', label: 'A-2 Sewing' },
    { building: 'A', floor: '3', label: 'A-3 Sewing' },
    { building: 'A', floor: '4', label: 'A-4 Sewing' },
    { building: 'A', floor: '5', label: 'A-5 Sewing' },
    { building: 'A', floor: 'Sample Room', label: 'A-Sample Room Sewing' },
    { building: 'B', floor: '1', label: 'B-1 Sewing' },
    { building: 'B', floor: '2', label: 'B-2 Sewing' },
    { building: 'B', floor: '3', label: 'B-3 Sewing' },
    { building: 'B', floor: '4', label: 'B-4 Sewing' },
    { building: 'B', floor: 'F', label: 'B-F Sewing' },
    { building: 'D', floor: '1', label: 'D-1 Sewing' },
    { building: 'PPA', floor: 'F', label: 'PPA-F Sewing' },
  ],
  BVG: [
    { building: 'B', floor: '3', label: 'B-3 Sewing' },
    { building: 'B', floor: '4', label: 'B-4 Sewing' },
    { building: 'C', floor: '3', label: 'C-3 Sewing' },
    { building: 'C', floor: '4', label: 'C-4 Sewing' },
    { building: 'C', floor: '5', label: 'C-5 Sewing' },
    { building: 'D', floor: '3', label: 'D-3 Sewing' },
    { building: 'D', floor: '4', label: 'D-4 Sewing' },
    { building: 'D', floor: '5', label: 'D-5 Sewing' },
    { building: 'E', floor: '3', label: 'E-3 Sewing' },
    { building: 'E', floor: '4', label: 'E-4 Sewing' },
    { building: 'E', floor: '5', label: 'E-5 Sewing' },
    { building: 'H', floor: 'F', label: 'H-F Sewing' },
    { building: 'LR', floor: '3', label: 'LR-3 Sewing' },
    { building: 'LR', floor: '4', label: 'LR-4 Sewing' },
    { building: 'S', floor: 'F', label: 'S-F Sewing' },
  ],
  BVN: [
    { building: 'K3', floor: 'K3-F1', label: 'K3-F1 Sewing' },
    { building: 'K3', floor: 'SUBCON', label: 'SUBCON Sewing' },
    { building: 'K4', floor: 'K4-F2', label: 'K4-F2 Sewing' },
    { building: 'K4', floor: 'K4-F3', label: 'K4-F3 Sewing' },
    { building: 'K5', floor: 'K5-F2', label: 'K5-F2 Sewing' },
    { building: 'K5', floor: 'K5-F3', label: 'K5-F3 Sewing' },
  ],
  BYS: [
    { building: '1', floor: '01', label: '1-01 Sewing' },
    { building: '1', floor: '02', label: '1-02 Sewing' },
    { building: '1', floor: '03', label: '1-03 Sewing' },
  ],
  PGV: [
    { building: 'P2 Sewing', floor: '1', label: 'P2 Sewing-1' },
    { building: 'P3 Sewing', floor: '2', label: 'P3 Sewing-2' },
  ],
};

export function buildingsFor(factory: FactoryCode): readonly string[] {
  const seen = new Set<string>();
  for (const entry of SEWING_FLOORS[factory]) {
    seen.add(entry.building);
  }
  return [...seen];
}

/** Distinct floor codes for the factory (for multi-select; not building-scoped). */
export function floorsFor(factory: FactoryCode): readonly string[] {
  const seen = new Set<string>();
  for (const entry of SEWING_FLOORS[factory]) {
    seen.add(entry.floor);
  }
  return [...seen];
}

function floorsIn(factory: FactoryCode, building: string): readonly FloorEntry[] {
  return SEWING_FLOORS[factory].filter((entry) => entry.building === building);
}

/**
 * A valid (building, floor) for this factory: the URL's pair if it exists, else the first floor of
 * the requested building, else the factory's first. There is no "all floors" — both bind as equality.
 */
export function resolveFloor(
  factory: FactoryCode,
  building: string | null,
  floor: string | null,
): FloorEntry {
  const all = SEWING_FLOORS[factory];
  const exact = all.find((entry) => entry.building === building && entry.floor === floor);
  if (exact !== undefined) {
    return exact;
  }
  const inBuilding = all.find((entry) => entry.building === building);
  // Non-null: every factory in SEWING_FLOORS has at least one floor, and the type says so.
  return inBuilding ?? all[0]!;
}

function options(values: readonly string[]): readonly FilterOption[] {
  return values.map((value) => ({ value, label: value }));
}

/**
 * The filter bar for one factory and one building; the floor options cascade off the building.
 * Call through `useMemo` keyed on both — a fresh array every render re-parses the URL every render.
 */
export function sewingFloorBoardFilters(
  factory: FactoryCode,
  selection: FloorEntry,
): FilterSchema {
  return [
    {
      type: 'select',
      name: 'factory',
      label: 'Factory',
      required: true,
      defaultValue: factory,
      options: FACTORY_OPTIONS,
    },
    {
      type: 'select',
      name: 'building',
      label: 'Building',
      required: true,
      defaultValue: selection.building,
      options: options(buildingsFor(factory)),
    },
    {
      type: 'select',
      name: 'floor',
      label: 'Floor',
      required: true,
      defaultValue: selection.floor,
      options: floorsIn(factory, selection.building).map((entry) => ({
        value: entry.floor,
        label: entry.label,
      })),
    },
    {
      type: 'date',
      name: 'tran_date',
      label: 'Day',
      required: true,
      defaultValue: defaultDate,
    },
  ];
}

/**
 * Which KPI family the board shows — `EINS` passed end-of-line QC, `SFTS` is the last sewing operation.
 * A declared server filter: it chooses which measure the queries return, not which lines are in the answer.
 */
export const METRIC_PARAM = 'metric';
const metricSchema = z.enum(['EINS', 'SFTS']);
export type Metric = z.infer<typeof metricSchema>;

export function parseMetric(raw: string | null): Metric {
  const parsed = metricSchema.safeParse(raw);
  return parsed.success ? parsed.data : 'EINS';
}

export const METRIC_TABS: readonly { value: Metric; label: string; hint: string }[] = [
  { value: 'EINS', label: 'Endline passed', hint: 'Pieces that passed end-of-line QC' },
  { value: 'SFTS', label: 'Sewing last operation', hint: 'Pieces off the last sewing operation' },
];

/**
 * Legacy's 120-second interval. Always on for this board (and the factory board) — see
 * `index.tsx` `WALL_POLL` and design-notes Refresh. Not an opt-in like Sewing Efficiency.
 */
export const REFRESH_INTERVAL_MS = 120 * 1000;
